import React, { useEffect, useState } from "react";
import { LifeBuoy, Bug, Lightbulb, HelpCircle, Loader2, CheckCircle2, MessageSquare } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

// iter132 Phase C — User-facing support page. Submit form on top,
// "My tickets" list below. Free tier allowed (deterministic user
// submission, no LLM/paid API per the standing tier-gating principle).

const TYPES = [
  { value: "bug",      label: "Bug",      icon: Bug,        color: "var(--bt-loss)" },
  { value: "idea",     label: "Idea",     icon: Lightbulb,  color: "var(--bt-accent)" },
  { value: "question", label: "Question", icon: HelpCircle, color: "var(--bt-text-muted)" },
];

const STATUS_COPY = {
  open:         { label: "Open",         cls: "bg-[var(--bt-accent)]/10 text-[var(--bt-accent)] border-[var(--bt-accent)]/30" },
  in_progress:  { label: "In Progress",  cls: "bg-yellow-500/10 text-yellow-500 border-yellow-500/30" },
  resolved:     { label: "Resolved",     cls: "bg-[var(--bt-win)]/10 text-[var(--bt-win)] border-[var(--bt-win)]/30" },
  closed:       { label: "Closed",       cls: "bg-[var(--bt-text-muted)]/10 text-[var(--bt-text-muted)] border-[var(--bt-border)]" },
};

export default function Support() {
  const [type, setType] = useState("bug");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  const canSubmit = title.trim().length >= 3 && description.trim().length >= 10 && !busy;

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/support/tickets");
      setTickets(data.tickets || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't load your tickets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      await api.post("/support/tickets", {
        type, title: title.trim(), description: description.trim(),
      });
      toast.success("Ticket submitted — we'll email you when there's an update");
      setTitle(""); setDescription("");
      await refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't submit");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8 max-w-3xl" data-testid="support-page">
      <div>
        <div className="flex items-center gap-2">
          <LifeBuoy className="w-6 h-6 text-[var(--bt-accent)]" />
          <p className="bt-label">Help & Feedback</p>
        </div>
        <h1 className="font-heading text-4xl">Support</h1>
        <p className="text-sm text-[var(--bt-text-muted)] mt-1">
          Report a bug, suggest an idea, or ask a question — we'll email you when there's an update.
        </p>
      </div>

      <form
        onSubmit={submit}
        className="bg-[var(--bt-surface)] border border-[var(--bt-border)] rounded-sm p-6 space-y-5"
        data-testid="support-submit-form"
      >
        <div>
          <Label className="bt-label block mb-1.5">Type</Label>
          <div className="flex gap-2" data-testid="support-type-group">
            {TYPES.map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => setType(t.value)}
                className={`flex items-center gap-1.5 px-4 py-2 border rounded-sm text-sm transition-colors ${
                  type === t.value
                    ? "border-[var(--bt-accent)] bg-[var(--bt-accent)]/10 text-[var(--bt-text)]"
                    : "border-[var(--bt-border)] text-[var(--bt-text-muted)] hover:border-[var(--bt-text-muted)]"
                }`}
                data-testid={`support-type-${t.value}`}
              >
                <t.icon className="w-4 h-4" style={{ color: t.color }} />
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label className="bt-label block mb-1.5">Title</Label>
          <Input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="One-line summary"
            maxLength={200}
            data-testid="support-title"
          />
        </div>

        <div>
          <Label className="bt-label block mb-1.5">Details</Label>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What happened, what you expected, and any steps to reproduce…"
            rows={5}
            maxLength={5000}
            data-testid="support-description"
          />
          <p className="text-xs text-[var(--bt-text-muted)] mt-1">
            {description.length} / 5000 characters
          </p>
        </div>

        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={!canSubmit}
            data-testid="support-submit"
            className="bg-[var(--bt-accent)] hover:bg-[var(--bt-accent-hover)] text-black font-bold"
          >
            {busy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Submitting…</> : "Submit ticket"}
          </Button>
        </div>
      </form>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-xl">My tickets</h2>
          <span className="text-sm text-[var(--bt-text-muted)]" data-testid="support-tickets-count">
            {tickets.length} {tickets.length === 1 ? "ticket" : "tickets"}
          </span>
        </div>

        {loading ? (
          <p className="text-sm text-[var(--bt-text-muted)]">Loading…</p>
        ) : tickets.length === 0 ? (
          <div className="border border-dashed border-[var(--bt-border)] rounded-sm p-8 text-center" data-testid="support-empty">
            <p className="text-sm text-[var(--bt-text-muted)]">No tickets yet.</p>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="support-my-tickets">
            {tickets.map((t) => {
              const statusCopy = STATUS_COPY[t.status] || STATUS_COPY.open;
              const typeMeta = TYPES.find((x) => x.value === t.type) || TYPES[2];
              return (
                <li
                  key={t.id}
                  data-testid={`support-ticket-${t.id}`}
                  className="border border-[var(--bt-border)] bg-[var(--bt-surface)] rounded-sm p-4 space-y-2"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <typeMeta.icon className="w-4 h-4 flex-none" style={{ color: typeMeta.color }} />
                      <p className="font-medium truncate">{t.title}</p>
                    </div>
                    <span className={`text-xs px-2 py-0.5 border rounded-sm ${statusCopy.cls}`}>
                      {statusCopy.label}
                    </span>
                  </div>
                  <p className="text-sm text-[var(--bt-text-muted)] whitespace-pre-wrap line-clamp-3">
                    {t.description}
                  </p>
                  {t.admin_reply && (
                    <div className="border-l-2 border-[var(--bt-accent)] pl-3 py-1 mt-2">
                      <p className="text-xs text-[var(--bt-text-muted)] flex items-center gap-1 mb-1">
                        <MessageSquare className="w-3 h-3" /> Support team wrote
                      </p>
                      <p className="text-sm whitespace-pre-wrap">{t.admin_reply}</p>
                    </div>
                  )}
                  <p className="text-xs text-[var(--bt-text-muted)]">
                    {new Date(t.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                  </p>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
